import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PageHero } from './DestinationsPages';
import { ThemesGrid } from '../components/sections/DestinationsGrid';
import WhyChooseUs from '../components/sections/WhyChooseUs';
import Testimonials from '../components/sections/Testimonials';
import EnquiryForm from '../components/sections/EnquiryForm';
import { CTABanner, PartnersStrip } from '../components/sections/CTASections';
import { Users, Award, Heart, MapPin, MessageCircle, Phone, Mail, Sparkles, ShieldCheck, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { COMPANY, saveEnquiry } from '../mock';
import CustomerVideoReviews from '../components/sections/CustomerVideoReviews';

export function AboutPage() {
  return (
    <div>
      <Helmet>
        <title>Why Choose Us | 3 Lakh+ Happy Travelers - 24x7 Travel Assistance</title>
        <meta name="description" content="3 Lakh+ Happy Travelers. 24x7 Travel Assistance. Learn about Mountains Chain Travel, North India's trusted travel experts since 2015." />
        <meta property="og:title" content="Why Choose Us | 3 Lakh+ Happy Travelers - 24x7 Travel Assistance" />
        <meta property="og:description" content="3 Lakh+ Happy Travelers. 24x7 Travel Assistance. Learn about Mountains Chain Travel, North India's trusted travel experts since 2015." />
      </Helmet>
      <PageHero title="About Mountains Chain" sub="Not just a trip. A memory for life. Since 2015." image="https://images.unsplash.com/photo-1605649487212-47bdab064df7?crop=entropy&cs=srgb&fm=jpg&q=85" />
      <CustomerVideoReviews />
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">Our Story</div>
          <h2 className="font-poppins text-[32px] lg:text-[40px] text-brand-blue leading-[1.1]">Born in the Himalayas, built for travelers.</h2>
          <p className="text-brand-blue/70 mt-5 text-[16px] leading-relaxed">Mountains Chain Travel started in 2015 with a small office in Shimla and a big idea - that travel should feel personal, not transactional. A decade later, we are a registered Pvt. Ltd. company with offices across North India, but the philosophy has not changed. Every traveler is a guest. Every trip is a story we want to be proud of.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Users, n: '3,00,000+', l: 'Happy Travelers' },
            { icon: MapPin, n: '180+', l: 'Curated Packages' },
            { icon: Award, n: '10+', l: 'Years of Service' },
            { icon: Heart, n: '4.8/5', l: 'Google Rating' },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-brand-grey rounded-2xl p-6 text-center">
                <Icon size={28} className="text-brand-orange mx-auto" />
                <div className="font-poppins font-bold text-brand-blue text-[28px] mt-3">{s.n}</div>
                <div className="text-brand-blue/60 text-[13px]">{s.l}</div>
              </div>
            );
          })}
        </div>
      </section>
      <div id="why-choose-us"><WhyChooseUs /></div>
      <Testimonials />
      <CTABanner />
      <PartnersStrip />
    </div>
  );
}

export function ContactPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', phone: '', destination: 'Himachal Pradesh', when: 'This Month', message: '' });
  const update = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: 'Please fill required fields', description: 'Name and phone are required.' });
      return;
    }
    await saveEnquiry(form);
    navigate('/thank-you', { state: form });
  };

  return (
    <div className="bg-white relative overflow-hidden">
      <Helmet>
        <title>Contact Us | Mountains Chain Travel</title>
        <meta name="description" content="Get in touch with Mountains Chain Travel. Connect with our specialists to plan your perfect Himalayan getaway." />
        <meta property="og:title" content="Contact Us | Mountains Chain Travel" />
        <meta property="og:description" content="Get in touch with Mountains Chain Travel. Connect with our specialists to plan your perfect Himalayan getaway." />
      </Helmet>
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-orange/5 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-brand-blue/5 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4 pointer-events-none" />
      
      <PageHero 
        title="Connect with Specialists" 
        sub="Your journey to the peaks starts with a simple conversation. We're available 24/7 for you." 
        image="https://images.unsplash.com/photo-1614591276564-7b3e69347a48?crop=entropy&cs=srgb&fm=jpg&q=85" 
      />
      <CustomerVideoReviews />
      
      <section className="max-w-7xl mx-auto px-6 py-20 lg:py-32 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          
          {/* Left Column: Interactive Contact Experience */}
          <div className="lg:col-span-5">
            <div className="mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-brand-blue text-white rounded-full text-[11px] font-bold uppercase tracking-[0.2em] mb-6 shadow-lg shadow-brand-blue/20">
                <Sparkles size={14} className="text-brand-orange" /> Direct Access
              </div>
              <h2 className="font-poppins text-[40px] lg:text-[52px] text-brand-blue font-black leading-[1] mb-6 tracking-tight">
                Skip the bots. <br/>
                <span className="text-brand-orange">Talk to experts.</span>
              </h2>
              <p className="text-brand-blue/70 text-[17px] leading-relaxed max-w-md font-medium">
                Our travel designers are mountaineers and locals who know every hidden trail and verified stay.
              </p>
            </div>

            <div className="space-y-4">
              <PremiumContactCard 
                icon={MessageCircle} 
                label="WhatsApp Specialists" 
                value="Instant Itinerary" 
                href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'d love to plan a trip with you.\n\nPlease share available tour packages and pricing.\n\nThank you!')}`}
                desc="Get quotes & plans in 5 mins"
                color="emerald"
              />
              <PremiumContactCard 
                icon={Phone} 
                label="Voice Consultation" 
                value={COMPANY.phone} 
                href={`tel:${COMPANY.phone}`}
                desc="Mon-Sat, 9 AM - 8 PM IST"
                color="orange"
              />
              <PremiumContactCard 
                icon={Mail} 
                label="Corporate & Bulk" 
                value={COMPANY.email} 
                href={`mailto:${COMPANY.email}`}
                desc="Detailed group proposals"
                color="blue"
              />
            </div>

            <div className="mt-12 p-8 rounded-[2rem] bg-brand-grey border border-brand-greyDark relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-24 h-24 bg-brand-blue/5 rounded-bl-[80px]" />
              <h4 className="font-poppins font-bold text-brand-blue text-lg mb-4 flex items-center gap-2">
                <MapPin size={20} className="text-brand-orange"/> Visit our Basecamp
              </h4>
              <p className="text-brand-blue/65 text-[14px] leading-relaxed mb-6">
                {COMPANY.address}
              </p>
              <div className="flex items-center gap-6">
                <div className="flex -space-x-3">
                  {[1,2,3].map(i => (
                    <img key={i} src={`https://i.pravatar.cc/100?img=${i+10}`} className="w-10 h-10 rounded-full border-2 border-white object-cover" alt="Team"/>
                  ))}
                  <div className="w-10 h-10 rounded-full bg-brand-orange text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">+12</div>
                </div>
                <div className="text-[12px] font-semibold text-brand-blue/50 uppercase tracking-wider">Meet the team in Shimla</div>
              </div>
            </div>
          </div>

          {/* Right Column: The Trip Planner Form */}
          <div className="lg:col-span-7">
            <div className="bg-white border-2 border-brand-blue/5 rounded-[3rem] p-8 lg:p-14 shadow-premium relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-orange/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-[3rem] pointer-events-none" />
              
              <div className="flex items-start justify-between mb-10 relative z-10">
                <div>
                  <h3 className="font-poppins font-black text-brand-blue text-[32px] leading-tight">Start Planning</h3>
                  <p className="text-brand-blue/50 font-medium mt-1">Tell us where you want to go</p>
                </div>
                <div className="w-16 h-16 rounded-2xl bg-brand-orange/10 flex items-center justify-center text-brand-orange">
                  <Heart size={32} fill="currentColor" className="opacity-20" />
                </div>
              </div>

              <form onSubmit={submit} className="space-y-8 relative z-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <StyledField label="Your Full Name">
                    <Input required placeholder="Rohan Sharma" className="premium-input" value={form.name} onChange={e => update('name', e.target.value)} />
                  </StyledField>
                  <StyledField label="WhatsApp / Phone">
                    <Input required placeholder="+91 9XXXXXXXXX" className="premium-input" value={form.phone} onChange={e => update('phone', e.target.value)} />
                  </StyledField>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <StyledField label="Preferred Destination">
                    <select className="premium-select" value={form.destination} onChange={e => update('destination', e.target.value)}>
                      <option>Himachal Pradesh</option>
                      <option>Kashmir Valley</option>
                      <option>Leh-Ladakh UT</option>
                      <option>Uttarakhand / Char Dham</option>
                      <option>International Tour</option>
                      <option>I'm Undecided</option>
                    </select>
                  </StyledField>
                  <StyledField label="Travel Month">
                    <select className="premium-select" value={form.when} onChange={e => update('when', e.target.value)}>
                      <option>This Month</option>
                      <option>Next Month</option>
                      <option>After 3 Months</option>
                      <option>Just Exploring</option>
                    </select>
                  </StyledField>
                </div>

                <StyledField label="Any specific requirements? (Optional)">
                  <Textarea placeholder="Honeymoon special decorations, bike rental needed, senior citizen friendly, specific budget etc..." className="premium-textarea" value={form.message} onChange={e => update('message', e.target.value)} />
                </StyledField>

                <div className="pt-4">
                  <Button type="submit" className="w-full h-16 bg-brand-blue hover:bg-brand-blueDark text-white rounded-2xl font-bold text-xl shadow-2xl shadow-brand-blue/30 transition-all hover:-translate-y-1 active:scale-[0.98] flex items-center justify-center gap-3 group/btn">
                    Get Custom Itinerary 
                    <ArrowRight size={22} className="group-hover/btn:translate-x-2 transition-transform text-brand-orange" />
                  </Button>
                </div>
                
                <div className="flex items-center justify-center gap-8 pt-4 border-t border-brand-greyDark">
                  <div className="flex items-center gap-2 text-[11px] font-bold text-brand-blue/40 uppercase tracking-widest">
                    <ShieldCheck size={14} className="text-emerald-500" /> Secure Data
                  </div>
                  <div className="flex items-center gap-2 text-[11px] font-bold text-brand-blue/40 uppercase tracking-widest">
                    <Award size={14} className="text-brand-orange" /> Govt. Approved
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Stylized Map Section */}
      <section className="px-6 pb-20">
        <div className="max-w-7xl mx-auto h-[500px] rounded-[3rem] overflow-hidden border-8 border-brand-grey shadow-2xl relative group">
          <iframe 
            title="Mountains Chain Travel Shimla" 
            className="w-full h-full grayscale contrast-[1.1] brightness-[1.02] transition-all duration-700 group-hover:grayscale-0" 
            loading="lazy" 
            src="https://maps.google.com/maps?q=Malyana%20Shimla%20Himachal%20Pradesh%20171006&t=&z=14&ie=UTF8&iwloc=&output=embed" 
          />
          <div className="absolute top-8 left-8 bg-brand-blue text-white px-8 py-5 rounded-[2rem] shadow-2xl border border-white/10 backdrop-blur-md">
            <h4 className="font-poppins font-bold text-lg mb-1">Our Basecamp</h4>
            <p className="text-white/60 text-xs uppercase tracking-widest font-bold">Shimla, Himachal Pradesh</p>
          </div>
        </div>
      </section>

      <EnquiryForm compact />
    </div>
  );
}

function PremiumContactCard({ icon: Icon, label, value, href, desc, color }) {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100 group-hover:border-emerald-500 shadow-emerald-500/5',
    orange: 'bg-orange-50 text-brand-orange border-orange-100 group-hover:border-brand-orange shadow-brand-orange/5',
    blue: 'bg-blue-50 text-brand-blue border-blue-100 group-hover:border-brand-blue shadow-brand-blue/5'
  };

  return (
    <a href={href} target="_blank" rel="noreferrer" className="block group">
      <div className={`flex items-center gap-6 p-6 rounded-[2rem] border-2 transition-all duration-300 bg-white ${colors[color] || colors.blue} hover:shadow-2xl hover:-translate-x-1`}>
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110 group-hover:rotate-3 ${color === 'emerald' ? 'bg-emerald-500 text-white' : color === 'orange' ? 'bg-brand-orange text-white' : 'bg-brand-blue text-white'}`}>
          <Icon size={24} />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-black uppercase tracking-[0.15em] opacity-50 mb-1">{label}</div>
          <div className="font-poppins font-bold text-[18px] text-brand-blue truncate">{value}</div>
          <div className="text-[13px] font-medium text-brand-blue/40 mt-0.5">{desc}</div>
        </div>
        <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity translate-x-4 group-hover:translate-x-0">
          <ArrowRight size={20} />
        </div>
      </div>
    </a>
  );
}

function StyledField({ label, children }) {
  return (
    <label className="block group/field">
      <span className="text-[13px] font-bold text-brand-blue/40 uppercase tracking-widest mb-3 block group-focus-within/field:text-brand-orange transition-colors">{label}</span>
      {children}
    </label>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[12.5px] font-semibold text-brand-blue/75 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

export function ThemesPage() {
  return (
    <div>
      <Helmet>
        <title>Travel by Theme | Mountains Chain Travel</title>
        <meta name="description" content="Find your perfect travel style with Mountains Chain Travel. Explore honeymoon, adventure, spiritual, or family travel themes tailored for you." />
        <meta property="og:title" content="Travel by Theme | Mountains Chain Travel" />
        <meta property="og:description" content="Find your perfect travel style with Mountains Chain Travel. Explore honeymoon, adventure, spiritual, or family travel themes tailored for you." />
      </Helmet>
      <PageHero title="Travel by Theme" sub="Find your perfect travel style - honeymoon, adventure, spiritual or family" image="https://images.unsplash.com/photo-1715457573748-8e8a70b2c1be?crop=entropy&cs=srgb&fm=jpg&q=85" />
      <CustomerVideoReviews />
      <ThemesGrid />
      <Testimonials />
      <EnquiryForm />
    </div>
  );
}
